import { useQuery } from "@apollo/client/react";
import { Card, Checkbox, Empty, Flex, Space, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetIngredientDeclarationDocument } from "../../gql/generated/graphqlSchema.tsx";
import { DeclarationView } from "../../components/DeclarationView.tsx";
import { NutritionDeclaration } from "../../components/NutritionDeclaration.tsx";
import { PageLoader } from "../../components/PageLoader.tsx";
import { useIngredient } from "./IngredientContext.tsx";

export const IngredientLabelTab = () => {
  const { t, i18n } = useTranslation();
  const { ingredient } = useIngredient();
  const [uppercaseAllergens, setUppercaseAllergens] = useState(false);
  const { data, loading } = useQuery(GetIngredientDeclarationDocument, {
    variables: { id: ingredient.id, versionNumber: ingredient.versionNumber },
  });
  const declaration = data?.ingredientDeclaration;

  const allergens = useMemo(() => {
    const derived = declaration?.derivedAllergens ?? [];
    return [...derived]
      .map((a) =>
        uppercaseAllergens
          ? t(`allergens.${a}`).toLocaleUpperCase(i18n.language)
          : t(`allergens.${a}`).toLocaleLowerCase(i18n.language),
      )
      .sort((a, b) => a.localeCompare(b, i18n.language));
  }, [declaration?.derivedAllergens, t, uppercaseAllergens, i18n.language]);

  if (loading) return <PageLoader />;

  const hasEntries = declaration != null && declaration.rootIndices.length > 0;

  if (!hasEntries && ingredient.nutritionProfile == null) {
    return <Empty description={t("ingredients.labelIngredients.noIngredients")} />;
  }

  return (
    <Flex gap="middle" vertical>
      <Space wrap>
        <Checkbox checked={uppercaseAllergens} onChange={(e) => setUppercaseAllergens(e.target.checked)}>
          {t("recipes.label.uppercaseAllergens")}
        </Checkbox>
      </Space>
      <Card>
        <Flex gap="middle" vertical>
          {hasEntries && declaration ? (
            <Typography.Text>
              <strong>{t("ingredients.labelIngredients.ingredientsPrefix")}</strong>{" "}
              <DeclarationView
                entries={declaration.entries}
                indices={declaration.rootIndices}
                uppercaseAllergens={uppercaseAllergens}
              />
            </Typography.Text>
          ) : null}
          {allergens.length > 0 ? (
            <Typography.Text>
              <strong>{t("ingredients.labelIngredients.allergensPrefix")}</strong> {allergens.join(", ")}
            </Typography.Text>
          ) : null}
          <NutritionDeclaration nutritionProfile={ingredient.nutritionProfile} />
        </Flex>
      </Card>
    </Flex>
  );
};
